import api from '../httpClient';

export interface FlowImportErrorDetail {
  flow?: string;
  question_order?: number;
  media_key?: string | number | null;
  key?: string | number | null;
  question_text?: string | null;
  error_type?: string;
  message: string;
  row_number?: number | null;
  line_number?: number | null;
  row_index?: number | string | null;
  // Compatibility aliases for older/in-memory progress payloads.
  flow_name?: string;
  question_key?: string;
  error_message?: string;
  csv_row_number?: number | null;
}

export interface ImportStatus {
  job_id: number;
  status: string;
  processed_items: number;
  total_items: number;
  error?: string;
  details?: string;
  error_details?: FlowImportErrorDetail[] | null;
  started_at?: string;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
}

export const importFromGoogleDrive = (data: {
  drive_url: string;
  course_name?: string;
  course_description?: string;
  learning_outcomes?: string[];
  replace_existing_media?: boolean;
}) =>
  api
    .post('/api/admin/content-import/import-from-drive/', data)
    .then((response) => response.data)
    .catch((error) => {
      console.error('Import request failed:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    });

export const importFromCsv = (data: {
  csv_file: File;
  course_name?: string;
  course_description?: string;
  learning_outcomes?: string[];
  replace_existing_media?: boolean;
}) => {
  const formData = new FormData();
  formData.append('csv_file', data.csv_file);
  if (data.course_name) {
    formData.append('course_name', data.course_name);
  }
  if (data.course_description) {
    formData.append('course_description', data.course_description);
  }
  if (Array.isArray(data.learning_outcomes)) {
    formData.append('learning_outcomes', JSON.stringify(data.learning_outcomes));
  }
  if (typeof data.replace_existing_media === 'boolean') {
    formData.append('replace_existing_media', data.replace_existing_media ? 'true' : 'false');
  }

  return api
    .post('/api/admin/content-import/import-from-csv/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error('Import request failed:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    });
};

export const getImportStatus = (
  jobId?: number,
  sinceTimestamp?: string,
): Promise<ImportStatus> => {
  const params: Record<string, string> = {};
  if (jobId) {
    params.job_id = jobId.toString();
  }
  if (sinceTimestamp) {
    params.since = sinceTimestamp;
  }

  return api
    .get<ImportStatus>('/api/admin/content-import/import-status/', { params })
    .then((response) => response.data)
    .catch((error) => {
      console.error('Import status request failed:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    });
};

export const cancelImport = (jobId: number) =>
  api
    .post('/api/admin/content-import/cancel-import/', { job_id: jobId })
    .then((response) => response.data)
    .catch((error) => {
      console.error('Cancel import request failed:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    });

export interface ImportJobSummary {
  id: number;
  status: string;
  import_type: string;
  source_type: string;
  course_name: string;
  started_at: string | null;
  completed_at: string | null;
  processed_items: number;
  total_items: number;
  error_count: number;
}

export const listImportJobs = (
  importType?: 'course' | 'flow',
): Promise<ImportJobSummary[]> =>
  api
    .get('/api/admin/content-import/list-jobs/', {
      params: importType ? { import_type: importType } : undefined,
    })
    .then((response) => response.data);

export const exportCourseCsv = async (courseId: number) => {
  try {
    return await api.get('/api/admin/content-import/export-course/', {
      params: { course_id: courseId },
      responseType: 'blob',
    });
  } catch (error) {
    const failure = error as { response?: { data?: unknown } };
    // Axios also returns JSON validation failures as blobs for download requests.
    if (failure.response?.data instanceof Blob) {
      try {
        failure.response.data = JSON.parse(await failure.response.data.text());
      } catch {
        // Preserve the original request error when an upstream response isn't JSON.
      }
    }
    throw error;
  }
};
